import { InputRef, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { EntryTextField } from 'components/common/form';
import { CreateFormLayout } from 'components/layouts';
import layoutStyles from 'components/layouts/CreateFormLayout/CreateFormLayout.module.scss';
import { FormStatusStepper, FullPageLoader } from 'components/ui';
import { routes } from 'config/routes';
import { useBasicFormStore } from 'stores/local';

import { MAP_STATUS_TO_COMPONENTS } from './config';

/**
 * Экран с шагами создания таблицы
 * @constructor
 */
const FormStepsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const store = useBasicFormStore();
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [showDelayedLoading, setShowDelayedLoading] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);
  const loadingTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (id) {
      store.fetchFormById(Number(id));
    }
  }, [id, store]);

  React.useEffect(() => {
    if (isEditingTitle) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isEditingTitle]);

  React.useEffect(() => {
    const loading = store.formUpdateRequest.loading;
    if (loading) {
      loadingTimerRef.current = setTimeout(() => setShowDelayedLoading(true), 500);
    } else {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowDelayedLoading(false);
    }
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [store.formUpdateRequest.loading]);

  const handleBlurSave = React.useCallback(async () => {
    await store.saveFormName();
    setIsEditingTitle(false);
  }, [store]);

  const handleSubmitAndClose = React.useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      await store.saveFormName();
      setIsEditingTitle(false);
    },
    [store]
  );

  if (!store.status) {
    return null;
  }

  if (store.formGetRequest.loading) {
    return <FullPageLoader />;
  }

  const {
    component,
    buttonNodes,
    context: Context,
  } = MAP_STATUS_TO_COMPONENTS[store.status];

  if (!component) {
    return null;
  }

  return (
    <Context>
      <CreateFormLayout
        pathBack={routes.form.createPath()}
        buttonNodes={buttonNodes}
        titleNode={
          isEditingTitle ? (
            <div className={layoutStyles.layout__headerLeftInputWrap}>
              <form onSubmit={handleSubmitAndClose} style={{ width: '100%', minWidth: 0 }}>
                <EntryTextField
                  ref={inputRef}
                  model={store.formName}
                  placeholder={t('common.enterText')}
                  onBlur={handleBlurSave}
                  className={layoutStyles.layout__headerLeftInput}
                />
              </form>
              {showDelayedLoading && (
                <div className={layoutStyles.layout__headerLeftInputLoader}>
                  <Spin size="small" />
                </div>
              )}
            </div>
          ) : (
            <span
              className={layoutStyles.layout__headerLeftTitle}
              onClick={() => setIsEditingTitle(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsEditingTitle(true);
                }
              }}
            >
              {store.formName.value || (t('common.enterText') as string)}
            </span>
          )
        }
      >
        <FormStatusStepper />
        {component}
      </CreateFormLayout>
    </Context>
  );
};

export default observer(FormStepsPage);
